/**
 * @copyright Copyright 2023-2023 Epic Systems Corporation
 * @file button for nomination apps
 * @module Epic.AppOrchard.Showroom.NominateButton
 */

import { Box, Button, ButtonProps, Link, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { ISuccessHandler, useAsync } from "@epic/react-async-hook";
import { config } from "ao/appConfig";
import { ConfirmationModal } from "ao/components/Core";
import { nominateApp } from "ao/components/Showroom/data/data";
import { IUserSecurity } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import React, { FunctionComponent, memo, useCallback } from "react";
import { BiHeart } from "react-icons/bi";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	userSecurity: IUserSecurity;
	setAppDetails: (x: IShowroomAppDownloadModel) => void;
	buttonProps?: ButtonProps;
	iconColor?: string;
	excludeIcon?: boolean;
}

export const NominateButton: FunctionComponent<IProps> = memo((props) => {
	const { appDetails, userSecurity, setAppDetails, buttonProps, iconColor, excludeIcon } = props;

	const toast = useToast();

	const { isOpen, onOpen, onClose } = useDisclosure();

	const handleSuccess = useCallback<ISuccessHandler<typeof nominateApp>>(
		(response) => {
			onClose();
			setAppDetails({
				...appDetails,
				interestedOn: response.InterestedOn,
				interestedCount: response.InterestedCount,
			});

			toast({
				title: "Success",
				description: "App successfully nominated",
				status: "success",
				duration: config.ToastDuration,
				isClosable: true,
			});
		},
		[appDetails, onClose, setAppDetails, toast],
	);

	const handleFailure = useCallback<ISuccessHandler<typeof nominateApp>>(() => {
		onClose();
		toast({
			title: "Error",
			description: "something went wrong",
			status: "error",
			duration: config.ToastDuration,
			isClosable: true,
		});
	}, [onClose, toast]);

	const [, handleNominateApp] = useAsync(nominateApp, {
		executeImmediately: false,
		displayName: "NominateApp",
		onSuccess: handleSuccess,
		onFailure: handleFailure,
	});

	const handleNominateClicked = useCallback(() => {
		handleNominateApp(appDetails.appId ?? -1);
	}, [appDetails.appId, handleNominateApp]);

	const computedButtonProps: ButtonProps = {
		colorScheme: buttonProps?.colorScheme || "showroomPrimary",

		fontWeight: buttonProps?.fontWeight || "normal",
		px: buttonProps?.px || buttonProps?.paddingX || 2,
		variant: buttonProps?.variant || "ghost",
		iconSpacing: buttonProps?.iconSpacing || "3px",
		...buttonProps,
	};

	return (
		<>
			<Button
				{...computedButtonProps}
				onClick={
					userSecurity.organizationGovernanceUrl || userSecurity.organizationGovernanceMessage
						? onOpen
						: handleNominateClicked
				}
				leftIcon={excludeIcon ? <BiHeart size={20} color={iconColor} /> : <></>}
				title="Let the person with Can Purchase Apps permission at your organization know you're interested in this product"
			>
				Nominate
			</Button>

			<ConfirmationModal
				title="Message From Your Organization"
				message={
					<Text>
						<Box>{userSecurity.organizationGovernanceMessage}</Box>
						<Link href={userSecurity.organizationGovernanceUrl} isExternal>
							{userSecurity.organizationGovernanceUrl}
						</Link>
					</Text>
				}
				isOpen={isOpen}
				onCancel={onClose}
				onAccept={handleNominateClicked}
				acceptCaption="Nominate"
				cancelCaption="Cancel"
				isCentered
			/>
		</>
	);
});
